import * as Schema from "effect/Schema";

import { CommandId, ThreadId, TrimmedNonEmptyString } from "./baseSchemas.ts";
import { DesignSystemCatalogEntry } from "./project.ts";

export const DesignSystemSlug = TrimmedNonEmptyString.check(
  Schema.isMaxLength(128),
  Schema.isPattern(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
);
export type DesignSystemSlug = typeof DesignSystemSlug.Type;

export const DesignSystemRevision = Schema.String.check(Schema.isPattern(/^[0-9a-f]{64}$/));
export type DesignSystemRevision = typeof DesignSystemRevision.Type;

export const DesignSystemReadModel = Schema.Struct({
  name: TrimmedNonEmptyString.check(Schema.isMaxLength(120)),
  slug: DesignSystemSlug,
  revision: DesignSystemRevision,
  relativePath: TrimmedNonEmptyString,
});
export type DesignSystemReadModel = typeof DesignSystemReadModel.Type;

/** UI collection id; Samples maps to on-disk `outputs/`. */
export const DesignCollectionId = Schema.Literals(["templates", "examples", "samples"]);
export type DesignCollectionId = typeof DesignCollectionId.Type;

export const DesignArtifactGroupSlug = TrimmedNonEmptyString.check(
  Schema.isMaxLength(128),
  Schema.isPattern(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
);
export type DesignArtifactGroupSlug = typeof DesignArtifactGroupSlug.Type;

export const DesignFileCount = Schema.Int.check(Schema.isGreaterThanOrEqualTo(0));
export type DesignFileCount = typeof DesignFileCount.Type;

/** One Design Artifact Group as the Delete picker lists it: identity plus the only countable detail. */
export const DesignArtifactGroup = Schema.Struct({
  collection: DesignCollectionId,
  groupSlug: DesignArtifactGroupSlug,
  /** Regular files anywhere under the group's storage directory. */
  fileCount: DesignFileCount,
});
export type DesignArtifactGroup = typeof DesignArtifactGroup.Type;

/**
 * What one Design System holds, keyed to `DesignListSystemsResult.systems` by slug.
 * Kept beside the read model rather than on it: this is volatile inventory, and the
 * read model is persisted into the Design command receipt journal.
 */
export const DesignSystemArtifactInventory = Schema.Struct({
  designSystemSlug: DesignSystemSlug,
  /** Regular files anywhere under the Design System's storage directory. */
  fileCount: DesignFileCount,
  artifactGroups: Schema.Array(DesignArtifactGroup),
});
export type DesignSystemArtifactInventory = typeof DesignSystemArtifactInventory.Type;

export const DesignInvalidSystem = Schema.Struct({
  relativePath: TrimmedNonEmptyString,
  detail: TrimmedNonEmptyString,
});
export type DesignInvalidSystem = typeof DesignInvalidSystem.Type;

export const DesignListSystemsInput = Schema.Struct({
  cwd: TrimmedNonEmptyString,
});
export type DesignListSystemsInput = typeof DesignListSystemsInput.Type;

export const DesignDeleteSystemInput = Schema.Struct({
  commandId: CommandId,
  cwd: TrimmedNonEmptyString,
  designSystemSlug: DesignSystemSlug,
  expectedRevision: DesignSystemRevision,
  /** Must match the current storage slug; binds confirmation to the exact system. */
  confirmationSlug: DesignSystemSlug,
  /** Must match the current display name; binds confirmation to the exact system. */
  confirmationName: TrimmedNonEmptyString.check(Schema.isMaxLength(120)),
});
export type DesignDeleteSystemInput = typeof DesignDeleteSystemInput.Type;

export const DesignListSystemsResult = Schema.Struct({
  systems: Schema.Array(DesignSystemReadModel),
  catalog: Schema.Array(DesignSystemCatalogEntry),
  invalidSystems: Schema.Array(DesignInvalidSystem),
  artifactInventory: Schema.Array(DesignSystemArtifactInventory),
});
export type DesignListSystemsResult = typeof DesignListSystemsResult.Type;

export const DesignDeleteSystemResult = Schema.Struct({
  designSystemSlug: DesignSystemSlug,
  slug: DesignSystemSlug,
  name: TrimmedNonEmptyString,
  relativePath: TrimmedNonEmptyString,
  verified: Schema.Literal(true),
  staged: Schema.Literal(false),
  agentConversationStarted: Schema.Literal(false),
});
export type DesignDeleteSystemResult = typeof DesignDeleteSystemResult.Type;

export const DesignDeleteCollectionGroupInput = Schema.Struct({
  commandId: CommandId,
  cwd: TrimmedNonEmptyString,
  designSystemSlug: DesignSystemSlug,
  expectedRevision: DesignSystemRevision,
  collection: DesignCollectionId,
  groupSlug: DesignArtifactGroupSlug,
  /** Must match groupSlug; binds confirmation to the exact artifact group. */
  confirmationGroupSlug: DesignArtifactGroupSlug,
});
export type DesignDeleteCollectionGroupInput = typeof DesignDeleteCollectionGroupInput.Type;

export const DesignDeleteCollectionGroupResult = Schema.Struct({
  designSystemSlug: DesignSystemSlug,
  collection: DesignCollectionId,
  groupSlug: DesignArtifactGroupSlug,
  relativePath: TrimmedNonEmptyString,
  verified: Schema.Literal(true),
  staged: Schema.Literal(false),
  agentConversationStarted: Schema.Literal(false),
});
export type DesignDeleteCollectionGroupResult = typeof DesignDeleteCollectionGroupResult.Type;

/**
 * Deletes a Design System that `design.listSystems` reported as invalid. Such a system has
 * no slug and no revision, so it is addressed by the project-relative path the listing gave,
 * echoed once for the staleness check `expectedRevision` performs on the healthy path.
 */
export const DesignDeleteInvalidSystemInput = Schema.Struct({
  commandId: CommandId,
  cwd: TrimmedNonEmptyString,
  relativePath: TrimmedNonEmptyString,
  /** Must match relativePath; binds the delete to the exact listing row. */
  confirmationRelativePath: TrimmedNonEmptyString,
});
export type DesignDeleteInvalidSystemInput = typeof DesignDeleteInvalidSystemInput.Type;

export const DesignDeleteInvalidSystemResult = Schema.Struct({
  /** The addressed path, echoed as sent. */
  relativePath: TrimmedNonEmptyString,
  /** The Design System storage directory actually removed. */
  storageRelativePath: TrimmedNonEmptyString,
  verified: Schema.Literal(true),
  staged: Schema.Literal(false),
  agentConversationStarted: Schema.Literal(false),
});
export type DesignDeleteInvalidSystemResult = typeof DesignDeleteInvalidSystemResult.Type;

export const DesignSystemFailure = Schema.Literals([
  "project-not-admitted",
  "design-workspace-unavailable",
  "system-not-found",
  "system-invalid",
  "system-not-invalid",
  "group-not-found",
  "duplicate-slug",
  "confirmation-mismatch",
  "idempotency-conflict",
  "revision-conflict",
  "project-busy",
  "unsafe-path",
  "operation-failed",
  "verification-failed",
  "unresolved-partial-mutation",
]);
export type DesignSystemFailure = typeof DesignSystemFailure.Type;

export class DesignSystemError extends Schema.TaggedErrorClass<DesignSystemError>()(
  "DesignSystemError",
  {
    cwd: TrimmedNonEmptyString,
    reason: DesignSystemFailure,
    message: TrimmedNonEmptyString,
    relativePath: Schema.optional(TrimmedNonEmptyString),
    designSystemSlug: Schema.optional(DesignSystemSlug),
    expectedRevision: Schema.optional(DesignSystemRevision),
    observedRevision: Schema.optional(DesignSystemRevision),
    recovery: Schema.optional(TrimmedNonEmptyString),
    cause: Schema.optional(Schema.Defect()),
  },
) {}

/**
 * One row the Delete picker checked, addressed the way its own operation needs.
 *
 * A Design System and a Design Artifact Group inside it are both guarded against
 * the `design.listSystems` reading the picker was built from, so a system whose
 * `DESIGN.md` moved on since is refused rather than deleted. An invalid system
 * has no revision to guard, so the listed path is the whole of its identity.
 *
 * `name` rides along on a system target for the same reason: it is what fills
 * `DesignDeleteSystemInput.confirmationName`, and deriving that server-side
 * would turn the check into a tautology.
 */
export const DesignDeleteTarget = Schema.Union([
  Schema.Struct({
    kind: Schema.Literal("system"),
    designSystemSlug: DesignSystemSlug,
    expectedRevision: DesignSystemRevision,
    name: TrimmedNonEmptyString.check(Schema.isMaxLength(120)),
  }),
  Schema.Struct({
    kind: Schema.Literal("artifact-group"),
    designSystemSlug: DesignSystemSlug,
    expectedRevision: DesignSystemRevision,
    collection: DesignCollectionId,
    groupSlug: DesignArtifactGroupSlug,
  }),
  Schema.Struct({
    kind: Schema.Literal("invalid-system"),
    relativePath: TrimmedNonEmptyString,
  }),
]);
export type DesignDeleteTarget = typeof DesignDeleteTarget.Type;

/**
 * One send of the Delete Design Operation.
 *
 * Delete is the one Design Operation with no Agent Provider turn, so it never
 * enters the Thread Queue: a filesystem `rm` waiting behind a running agent turn
 * would misrepresent both. It is still one *act*, which is why the whole
 * selection arrives together and `threadId` says where its single report lands.
 *
 * The server fans this out to one delete per target, each deriving its own
 * `commandId` from this one, so `design-command-receipts.json` stays idempotent
 * per target and a resent submission replays onto the same receipts. Doing the
 * fan-out here rather than in the client is what makes the report atomic with
 * the deletions: a client that dies mid-fan-out would otherwise leave destroyed
 * work with no trace in the conversation.
 */
export const DesignDeleteTargetsInput = Schema.Struct({
  commandId: CommandId,
  cwd: TrimmedNonEmptyString,
  /** The Design thread whose conversation receives the one report. */
  threadId: ThreadId,
  targets: Schema.Array(DesignDeleteTarget).check(Schema.isMinLength(1)),
});
export type DesignDeleteTargetsInput = typeof DesignDeleteTargetsInput.Type;

/**
 * What became of one target. Every target is attempted and every outcome
 * reported: all-or-nothing is a lie the moment `rm` has run on the first one,
 * and pre-flighting every revision only moves the race earlier while adding a
 * failure mode where nothing happens because one unrelated group went stale.
 *
 * A target that was already gone is `deleted`. That is the state the user asked
 * for, and the receipts journal exists precisely so a replayed delete lands there.
 */
export const DesignDeleteTargetOutcome = Schema.Struct({
  target: DesignDeleteTarget,
  status: Schema.Literals(["deleted", "refused"]),
  /** Why a refused target was left alone. */
  reason: Schema.optional(DesignSystemFailure),
  /** The storage directory actually removed, on a target that was deleted. */
  relativePath: Schema.optional(TrimmedNonEmptyString),
});
export type DesignDeleteTargetOutcome = typeof DesignDeleteTargetOutcome.Type;

/**
 * The single host-authored record of one Delete. It is both the RPC's result and
 * the payload of the one `design.delete.reported` Thread Activity the fan-out
 * appends, so the conversation and the caller never disagree about what happened.
 *
 * Counts are not carried: they are `outcomes.length` and how many of those are
 * `deleted`, and a stored count is a second answer that can go stale.
 */
export const DesignDeleteReport = Schema.Struct({
  commandId: CommandId,
  outcomes: Schema.Array(DesignDeleteTargetOutcome),
});
export type DesignDeleteReport = typeof DesignDeleteReport.Type;

export const DesignDeleteTargetsResult = DesignDeleteReport;
export type DesignDeleteTargetsResult = typeof DesignDeleteTargetsResult.Type;
